<template>
  <ReportCellWrapper
    :trial-over="params.data?.trialOver"
    :is-disabled-by-tier="params.data?.isDisabledByTier"
    :use-tooltip="!!params.data?.trialOver && !params.data?.isDisabledByTier"
    :class="{
      'cursor-not-allowed': !!params.data?.trialOver || isDisabled,
    }"
    class="flex flex-row items-center max-w-full"
  >
    <FontAwesomeIcon
      :icon="['fak', params.data?.isFolder ? 'folder' : 'list']"
      class="text-neutral-accent h-16 w-16 mr-8"
    />
    <div
      :class="{
        'w-[76.5%]': isInErrorState,
        'w-full': !isInErrorState,
        'pointer-events-none': !!params.data?.trialOver || isDisabled,
      }"
      class="flex flex-col justify-center gap-4 min-w-0"
    >
      <BittsTooltip
        class="min-w-0 max-w-full"
        :mount-to-body="true"
        placement="bottom"
        default-slot-classes="report-name-cell__tooltip-trigger"
        :is-trigger-focusable="isReportNameTooltipShown"
        :show-tooltip="isReportNameTooltipShown ? null : false"
      >
        <component
          :is="reportLink && !isDisabled ? RouterLink : 'div'"
          :ref="setReportNameEl"
          :to="reportLink && !isDisabled ? reportLink : undefined"
          class="report-name-cell__report-name"
          :class="{ 'max-w-[calc(100%-90px)]': isDisabled }"
        >
          {{ reportName }}
        </component>
        <UpsellTooltip
          v-if="isDisabled"
          class="shrink-0 pointer-events-auto"
          :billing-interaction="{
            cta: LISTS_CTAS.CREATE_LIST,
            event_site: EVENT_SITES.LIST_TABLE_UPSELL_OVERLAY,
          }"
          @click.stop
        >
          <template #title> Upgrade to access this List </template>
          <BittsTag size="x-small" color="upsell">
            <FontAwesomeIcon
              :icon="['fak', 'upgrade']"
              class="text-upsell-accent mr-2"
            />
            Upgrade
          </BittsTag>
        </UpsellTooltip>
        <template #title>
          {{ reportName }}
        </template>
      </BittsTooltip>
      <OverflowTooltip
        :text="folderDescription"
        :text-class="folderNameClass"
      />
    </div>
    <BittsTooltip
      v-if="isInErrorState && !isDisabled"
      :class="{ 'pointer-events-none': !!params.data?.trialOver }"
      :mount-to-body="true"
      placement="bottom"
    >
      <FontAwesomeIcon
        :icon="['fas', 'diamond-exclamation']"
        class="text-danger-accent h-16 w-24"
      />
      <template #title>
        {{ errorTooltipText }}
      </template>
    </BittsTooltip>
  </ReportCellWrapper>
</template>

<script setup lang="ts">
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';
import { EVENT_SITES, LISTS_CTAS } from '@crossbeam/itly';
import { Nullable } from '@crossbeam/types';

import { ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { storeToRefs } from 'pinia';
import { type ComponentPublicInstance, computed, ref } from 'vue';
import { RouterLink } from 'vue-router';

import UpsellTooltip from '@/components/billing/UpsellTooltip.vue';
import ReportCellWrapper from '@/components/reports/ReportCellWrapper.vue';
import OverflowTooltip from '@/components/shared/OverflowTooltip.vue';

import useAuth from '@/composables/useAuth';
import { useIsOverflowing } from '@/composables/useIsOverflowing';
import {
  INVALID_OWN_FILTERS,
  INVALID_PARTNER_FILTERS,
  NO_OWN_POPULATIONS,
  NO_PARTNER_POPULATIONS,
  REPORT_ERROR_STATES,
  type ReportErrorState,
} from '@/constants/reports';
import { useReportsStore } from '@/stores';
import { ReportCell, TrimmedReport } from '@/types/reports';

const { params } = defineProps<{
  params: ICellRendererParams<ReportCell>;
}>();

const { currentOrg } = useAuth();

const ERROR_STATE_TO_TOOLTIP_TEXT: Record<ReportErrorState, string> = {
  [REPORT_ERROR_STATES.NO_OWN_POPULATIONS]:
    'You have no populations in this List. Add populations to this List to view records',
  [REPORT_ERROR_STATES.NO_PARTNER_POPULATIONS]:
    "There are no partner's populations in this List. Edit this List and add partner populations to view records",
  [REPORT_ERROR_STATES.BOTH_FILTERS_INVALID]:
    "Some of your filters were removed because your partner's data sharing settings and the fields you are syncing changed in this List",
  [REPORT_ERROR_STATES.INVALID_OWN_FILTERS]:
    'Some of your filters were removed because the fields you are syncing from your data source changed in this List',
  [REPORT_ERROR_STATES.INVALID_PARTNER_FILTERS]:
    "Some of your filters were removed because your partner's data sharing settings changed in this List",
};

const EXTERNAL_SHARE_UNAVAILABLE_TOOLTIP =
  "This List is no longer available. It used data from a field that no longer exists, so the List can't be displayed.";

const isReportSharedWithCurrentOrg = computed(() => {
  if (params.data?.isStaticList || params.data?.isFolder) return false;
  return params.data?.report?.shared_partner_org_id === currentOrg.value?.id;
});

const errorTooltipText = computed(() => {
  if (!errorState.value) return '';
  if (params.data?.isStaticList || params.data?.isFolder) return '';

  // Show special message for external partners viewing shared reports with invalid filters
  // Check raw errors array to catch INVALID_OWN_FILTERS even when BOTH_FILTERS_INVALID
  const rawErrors =
    (params.data?.report as TrimmedReport)?.status?.errors?.map(
      (err: { type: string }) => err.type,
    ) || [];
  const hasInvalidOwnFilters = rawErrors.includes(INVALID_OWN_FILTERS);

  if (isReportSharedWithCurrentOrg.value && hasInvalidOwnFilters) {
    return EXTERNAL_SHARE_UNAVAILABLE_TOOLTIP;
  }

  return ERROR_STATE_TO_TOOLTIP_TEXT[errorState.value];
});

const isDisabled = computed(() => params.data?.isDisabledByTier ?? false);
const isInErrorState = computed(
  () =>
    !params.data?.isStaticList &&
    !params.data?.isFolder &&
    params.data?.report?.status?.errors?.length,
);
const errors = computed(() => {
  if (!isInErrorState.value) return [];
  if (params.data?.isStaticList || params.data?.isFolder) return [];
  return (
    params.data?.report?.status?.errors?.map(
      (err: { type: string }) => err.type,
    ) || []
  );
});
const errorState = computed<Nullable<ReportErrorState>>(() => {
  if (!isInErrorState.value) return null;
  if (errors.value?.includes(NO_OWN_POPULATIONS)) return NO_OWN_POPULATIONS;
  if (errors.value?.includes(NO_PARTNER_POPULATIONS))
    return NO_PARTNER_POPULATIONS;
  const invalidOwnFilters = errors.value?.includes(INVALID_OWN_FILTERS);
  if (invalidOwnFilters && errors.value?.includes(INVALID_PARTNER_FILTERS))
    return REPORT_ERROR_STATES.BOTH_FILTERS_INVALID;
  return invalidOwnFilters ? INVALID_OWN_FILTERS : INVALID_PARTNER_FILTERS;
});

const { folders } = storeToRefs(useReportsStore());
const folderId = computed(() =>
  params.data?.isStaticList || params.data?.isFolder
    ? null
    : params.data?.report?.folder_uuid,
);
const reportName = computed(() => params.data?.report?.name);
const reportLink = computed(() =>
  params.data?.trialOver ? undefined : params.data?.reportLink,
);
function getDescriptionText(uuid: string) {
  if (params.data?.isFolder)
    return `${params.data?.report?.reports.length} ${params.data?.report?.reports.length === 1 ? 'List' : 'Lists'}`;
  const folder = folders.value.find((folder) => folder.id === uuid);
  return folder?.name || 'Unfiled';
}

const folderDescription = computed(
  () => getDescriptionText(folderId.value || '') || 'Unfiled',
);
const folderNameClass = computed(() =>
  folderId.value
    ? 'report-name-cell__folder-name text-sm'
    : 'report-name-cell__folder-name text-sm report-name-cell__unfiled',
);

const reportNameEl = ref<HTMLElement | null>(null);
function setReportNameEl(el: Element | ComponentPublicInstance | null) {
  reportNameEl.value =
    el && '$el' in el ? (el.$el as HTMLElement) : (el as HTMLElement | null);
}
const { isOverflowing: isReportNameOverflowing } = useIsOverflowing(
  reportNameEl,
  () => reportName.value,
);
const isReportNameTooltipShown = computed(
  () =>
    isReportNameOverflowing.value && !isInErrorState.value && !isDisabled.value,
);
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.report-name-cell__folder-name {
  @apply text-neutral-text truncate;
  max-width: 140px;
}

.report-name-cell__report-name {
  @apply rounded-bts-md max-w-full truncate text-base;
}
a.report-name-cell__report-name {
  @apply text-inherit no-underline;
}
a.report-name-cell__report-name:focus-visible {
  @apply outline outline-2 -outline-offset-2 outline-neutral-border-focus;
}

.report-name-cell__tooltip-trigger {
  @apply flex items-center gap-8 min-w-0 max-w-full;
}

.report-name-cell__unfiled {
  @apply leading-none;
}
</style>
